import { useEffect, useRef, useState } from 'react';
import { TravparkLogo } from './TravparkLogo';
import { sharedCrawlPos } from './OverlayCrawlBanner';

// Shared camera rect so the webcam layer in Overlay.jsx matches the frame hole exactly
export const STUDIO_CAM_RECT = {
  top: '2.5vh',
  left: '1.5vw',
  width: '76vw',
  height: '76vh',
};

function StudioClock({ startTime }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  let startStr = '';
  if (startTime) {
    try { startStr = new Date(startTime).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }); } catch {}
  }
  return (
    <div data-testid="studio-clock" style={{
      backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
      borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '2rem', fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>
        {now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>
      {startStr && (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Neste start
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.3rem', fontWeight: 700, color: 'var(--theme-accent)', lineHeight: 1.1 }}>
            {startStr}
          </div>
        </div>
      )}
    </div>
  );
}

function StudioOddsPanel({ sortedHorses, gameType, legNumber, flashingHorses, renderDistArrow, allRaces, currentLeg }) {
  const rotLegs = (allRaces || []).filter(r => r.horses?.length).map(r => r.leg_number);
  const canRotate = rotLegs.length > 1;
  const [rotIndex, setRotIndex] = useState(0);

  useEffect(() => {
    if (!canRotate) return;
    const t = setInterval(() => setRotIndex(prev => prev + 1), 10000);
    return () => clearInterval(t);
  }, [canRotate, rotLegs.length]);

  // Resolve which leg to display (auto-rotation cycles all legs, 10s each)
  let displayLeg = legNumber;
  let horses = sortedHorses;
  let isLive = true;
  if (canRotate) {
    displayLeg = rotLegs[rotIndex % rotLegs.length];
    const race = (allRaces || []).find(r => r.leg_number === displayLeg);
    horses = (race?.horses || []).slice().sort((a, b) => b.distribution - a.distribution).slice(0, 12);
    isLive = displayLeg === currentLeg;
    if (isLive && sortedHorses.length) horses = sortedHorses;
  }

  return (
    <div data-testid="studio-odds-panel" style={{
      backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
      borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
    }}>
      <div style={{
        background: `linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)`,
        borderBottom: '2px solid var(--theme-panel-border)', padding: '7px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.3rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-accent)' }}>
          {gameType}-{displayLeg}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {canRotate && (
            <div data-testid="studio-odds-counter" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>
              {rotLegs.indexOf(displayLeg) + 1}/{rotLegs.length}
            </div>
          )}
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Direkteodds
          </div>
        </div>
      </div>
      <div key={displayLeg} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, animation: canRotate ? 'studio-ticker-up 0.5s cubic-bezier(0.22, 1, 0.36, 1)' : 'none' }}>
        {horses.length === 0 && (
          <div style={{ padding: '16px', fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
            Venter på løpsdata...
          </div>
        )}
        {horses.slice(0, 8).map((horse, index) => (
          <div
            key={horse.number}
            className={isLive && flashingHorses?.has(horse.number) ? 'flash-update' : ''}
            style={{
              display: 'flex', alignItems: 'center', padding: '6px 12px',
              borderBottom: index < 7 ? '1px solid var(--theme-panel-border-faint)' : 'none',
              backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
            }}
          >
            <div style={{
              width: '30px', height: '30px', backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-dark)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '1rem',
              borderRadius: '5px', flexShrink: 0,
            }}>
              {horse.number}
            </div>
            <div style={{ flex: 1, marginLeft: '9px', overflow: 'hidden' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.98rem', color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.15 }}>
                {horse.name}
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflowState: 'ellipsis' }}>
                {horse.driver}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '52px' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.88rem', color: 'var(--theme-accent)', fontWeight: 700 }}>
                {horse.distribution.toFixed(1)}%{isLive ? renderDistArrow(horse.number) : null}
              </div>
              <div style={{ width: '44px', height: '4px', backgroundColor: `rgba(var(--theme-accent-rgb), 0.2)`, borderRadius: '3px', overflow: 'hidden', marginTop: '2px' }}>
                <div style={{ height: '100%', width: `${Math.min(horse.distribution * 2, 100)}%`, backgroundColor: 'var(--theme-accent)' }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StudioMessage({ messageText, fullWidth }) {
  const messages = String(messageText || '').split('\n').map(s => s.trim()).filter(Boolean);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
    if (messages.length <= 1) return;
    const t = setInterval(() => setIdx(prev => (prev + 1) % messages.length), 10000);
    return () => clearInterval(t);
  }, [messageText, messages.length]);

  if (messages.length === 0) return null;
  const current = messages[idx % messages.length];

  return (
    <div
      data-testid="studio-message"
      style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        height: '100%', overflow: 'hidden',
        ...(fullWidth
          ? { flex: 1 }
          : { flexShrink: 0, maxWidth: '38%', paddingRight: '22px', borderRight: '2px solid var(--theme-panel-border-subtle)' }),
      }}
    >
      <div style={{
        backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-dark)',
        fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.2rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        padding: '3px 12px', borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        Beskjed
      </div>
      <div
        key={idx}
        style={{
          fontFamily: "'Inter', sans-serif", fontSize: '1.25rem', fontWeight: 600,
          color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          animation: 'studio-ticker-up 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {current}
      </div>
      {messages.length > 1 && (
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {(idx % messages.length) + 1}/{messages.length}
        </div>
      )}
    </div>
  );
}

function StudioCrawl({ crawlText }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const frameRef = useRef(null);
  const lastTimeRef = useRef(null);

  useEffect(() => {
    lastTimeRef.current = null;
    if (sharedCrawlPos.text !== crawlText) {
      sharedCrawlPos.x = null;
      sharedCrawlPos.text = crawlText;
    }
    const speed = 80;
    const animate = (ts) => {
      if (!textRef.current || !containerRef.current) { frameRef.current = requestAnimationFrame(animate); return; }
      if (!lastTimeRef.current) lastTimeRef.current = ts;
      const dt = (ts - lastTimeRef.current) / 1000;
      lastTimeRef.current = ts;
      const textW = textRef.current.scrollWidth;
      const boxW = containerRef.current.offsetWidth;
      if (sharedCrawlPos.x === null) sharedCrawlPos.x = boxW;
      sharedCrawlPos.x -= speed * dt;
      if (sharedCrawlPos.x < -textW) sharedCrawlPos.x = boxW;
      textRef.current.style.transform = `translateX(${sharedCrawlPos.x}px)`;
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [crawlText]);

  const parts = String(crawlText || '').split(/((?:V\d+|DD)-\d+:)/g);
  const renderedText = parts.map((part, i) =>
    /^(?:V\d+|DD)-\d+:$/.test(part)
      ? <span key={i} style={{ color: 'var(--theme-accent)', fontWeight: 700 }}>{part}</span>
      : part
  );

  return (
    <div ref={containerRef} data-testid="studio-crawl" style={{ position: 'relative', flex: 1, height: '100%', overflow: 'hidden' }}>
      <div
        ref={textRef}
        style={{
          position: 'absolute', top: '50%', marginTop: '-24px',
          whiteSpace: 'nowrap', color: '#FFFFFF',
          fontSize: '34px', lineHeight: '48px',
          fontFamily: "'Inter', sans-serif", fontWeight: 500,
          willChange: 'transform',
        }}
      >
        {renderedText}
      </div>
    </div>
  );
}

function StudioBongCard({ bongData, bongSelectedLegs, gameType }) {
  if (!bongData) return null;
  const legs = bongData.legs || {};
  const legNumbers = Object.keys(legs).map(Number).sort((a, b) => a - b);
  const visibleLegs = bongSelectedLegs?.length > 0
    ? legNumbers.filter(l => bongSelectedLegs.includes(l))
    : legNumbers;
  const hasData = visibleLegs.some(l => String(legs[l] || '').trim() !== '');
  if (!hasData) return null;

  return (
    <div
      data-testid="studio-bong-card"
      style={{
        backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
        borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        flex: 1, minHeight: 0,
      }}
    >
      <div style={{
        background: `linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)`,
        borderBottom: '2px solid var(--theme-panel-border)', padding: '7px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{
          fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.25rem', fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--theme-accent)',
        }}>
          Bongen
        </div>
        {bongData.ownerName && (
          <div style={{
            fontFamily: "'Inter', sans-serif", fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '65%',
          }}>
            {bongData.ownerName}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {visibleLegs.map((legNum, index) => {
          const selection = String(legs[legNum] || '').trim();
          const isBanker = selection && !selection.includes(',') && selection.length > 0;
          return (
            <div
              key={legNum}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '5px 12px', flex: 1, minHeight: 0,
                borderBottom: index < visibleLegs.length - 1 ? '1px solid var(--theme-panel-border-faint)' : 'none',
                backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
              }}
            >
              <div style={{
                backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-dark)',
                fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.95rem', fontWeight: 700,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                padding: '3px 9px', borderRadius: '5px', whiteSpace: 'nowrap', flexShrink: 0,
                minWidth: '52px', textAlign: 'center',
              }}>
                {gameType || 'Avd'}-{legNum}
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: '1.1rem', fontWeight: 700,
                color: selection ? '#FFFFFF' : 'rgba(255,255,255,0.25)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
              }}>
                {selection || '—'}
              </div>
              {isBanker && (
                <div style={{
                  fontFamily: "'Inter', sans-serif", fontSize: '0.65rem', fontWeight: 700,
                  color: 'var(--theme-accent-dark)', backgroundColor: 'var(--theme-accent)',
                  padding: '2px 7px', borderRadius: '4px', textTransform: 'uppercase',
                  letterSpacing: '0.08em', flexShrink: 0,
                }}>
                  B
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StudioOddsTicker({ sortedHorses, gameType, legNumber, flashingHorses, renderDistArrow }) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const frameRef = useRef(null);
  const lastTimeRef = useRef(null);
  const xRef = useRef(null);

  useEffect(() => {
    lastTimeRef.current = null;
    xRef.current = null;
    const speed = 110;
    const animate = (ts) => {
      if (!trackRef.current || !containerRef.current) { frameRef.current = requestAnimationFrame(animate); return; }
      if (!lastTimeRef.current) lastTimeRef.current = ts;
      const dt = (ts - lastTimeRef.current) / 1000;
      lastTimeRef.current = ts;
      const trackW = trackRef.current.scrollWidth;
      const boxW = containerRef.current.offsetWidth;
      if (xRef.current === null) xRef.current = boxW;
      xRef.current -= speed * dt;
      if (xRef.current < -trackW) xRef.current = boxW;
      trackRef.current.style.transform = `translateX(${xRef.current}px)`;
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [sortedHorses.length]);

  if (!sortedHorses?.length) return null;

  return (
    <div
      ref={containerRef}
      data-testid="studio-odds-ticker"
      style={{ position: 'relative', flex: 1, height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center' }}
    >
      {/* Fixed leading label */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: '10px',
        paddingRight: '18px', marginRight: '18px', height: '60%',
        borderRight: '2px solid var(--theme-panel-border-subtle)',
        zIndex: 2, backgroundColor: 'var(--theme-panel-bg)',
      }}>
        <div style={{
          fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.7rem', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--theme-accent)', whiteSpace: 'nowrap',
        }}>
          {gameType || 'Odds'}-{legNumber}
        </div>
        <div style={{
          fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)',
          textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap',
        }}>
          Direkteodds
        </div>
      </div>
      <div style={{ position: 'relative', flex: 1, height: '100%', overflow: 'hidden' }}>
        <div
          ref={trackRef}
          style={{
            position: 'absolute', top: '50%', marginTop: '-22px', height: '44px',
            display: 'flex', alignItems: 'center', gap: '30px',
            whiteSpace: 'nowrap', willChange: 'transform',
          }}
        >
          {sortedHorses.map(horse => (
            <div
              key={horse.number}
              className={flashingHorses?.has(horse.number) ? 'flash-update' : ''}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}
            >
              <div style={{
                width: '32px', height: '32px', backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-dark)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '1.05rem',
                borderRadius: '5px', flexShrink: 0,
              }}>
                {horse.number}
              </div>
              <div style={{
                fontFamily: "'Inter', sans-serif", fontSize: '1.05rem', fontWeight: 600,
                color: '#FFFFFF', whiteSpace: 'nowrap',
              }}>
                {horse.name}
              </div>
              <div style={{
                fontFamily: "'Inter', sans-serif", fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)',
                whiteSpace: 'nowrap',
              }}>
                ({horse.driver})
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: '1.05rem', fontWeight: 700,
                color: 'var(--theme-accent)', whiteSpace: 'nowrap',
              }}>
                {horse.distribution.toFixed(1)}%{renderDistArrow(horse.number)}
              </div>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: 'var(--theme-panel-border-subtle)', flexShrink: 0,
              }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StudioWeatherStrip({ weatherData }) {
  return (
    <div data-testid="studio-weather-strip" style={{ display: 'flex', alignItems: 'center', gap: '28px', flex: 1, overflow: 'hidden' }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '2.4rem', fontWeight: 700, color: 'var(--theme-accent)', whiteSpace: 'nowrap' }}>
        {weatherData.temperature}°
      </div>
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
        {weatherData.weather_description}
      </div>
      <div style={{ width: '2px', height: '50%', backgroundColor: 'var(--theme-panel-border-subtle)' }} />
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.15rem', color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
        Vind: <span style={{ color: '#FFFFFF', fontWeight: 600 }}>{weatherData.wind_speed} m/s {weatherData.wind_direction_text}</span>
      </div>
      {weatherData.humidity !== undefined && (
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.15rem', color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
          Luftfuktighet: <span style={{ color: '#FFFFFF', fontWeight: 600 }}>{weatherData.humidity}%</span>
        </div>
      )}
      {weatherData.precipitation !== undefined && (
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.15rem', color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
          Nedbør: <span style={{ color: '#FFFFFF', fontWeight: 600 }}>{weatherData.precipitation} mm</span>
        </div>
      )}
      <div style={{ flex: 1 }} />
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
        Værmelding
      </div>
    </div>
  );
}

function StudioSponsorCard({ sponsors }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    if (sponsors.length <= 1) return;
    const t = setInterval(() => setIdx(prev => (prev + 1) % sponsors.length), 10000);
    return () => clearInterval(t);
  }, [sponsors.length]);
  if (sponsors.length === 0) return null;
  const s = sponsors[idx % sponsors.length];
  return (
    <div data-testid="studio-sponsor-card" style={{
      backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
      borderRadius: '10px', padding: '8px 12px 10px', flexShrink: 0, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px',
        fontFamily: "'Inter', sans-serif", fontSize: '0.62rem', letterSpacing: '0.15em',
        textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
      }}>
        <span>Sponsor</span>
        {sponsors.length > 1 && <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{(idx % sponsors.length) + 1}/{sponsors.length}</span>}
      </div>
      <div style={{ borderRadius: '6px', backgroundColor: '#FFFFFF', height: '9vh', overflow: 'hidden' }}>
        <div
          key={s.id}
          style={{
            width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'studio-ticker-up 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <img src={s.url} alt={s.name} style={{ maxWidth: '88%', maxHeight: '82%', objectFit: 'contain' }} />
        </div>
      </div>
    </div>
  );
}

function StudioWeatherCard({ weatherData }) {
  if (!weatherData) return null;
  return (
    <div data-testid="studio-weather-card" style={{
      backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
      borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0,
    }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.9rem', fontWeight: 700, color: 'var(--theme-accent)', lineHeight: 1 }}>
        {weatherData.temperature}°
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.9rem', fontWeight: 600, color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {weatherData.weather_description}
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
          {weatherData.wind_speed} m/s {weatherData.wind_direction_text} vind
        </div>
      </div>
    </div>
  );
}

/**
 * StudioFrame - Sky News-style layout: shrunken camera window top-left,
 * graphics column on the right and info strip below the camera.
 * Chromakey-safe: transform-only animations, no opacity transitions.
 */
export function StudioFrame({
  isVmix, useWebcam, sortedHorses, gameType, legNumber, trackName,
  raceDistance, startMethod, startTime, showOdds, showBong, bongData, bongDataGuest, showDualBong,
  bongSelectedLegs, weatherData, flashingHorses, renderDistArrow, sommertravetMode,
  showCrawl, crawlText, showWeather, allRaces, currentLeg, showMessage, messageText,
  sponsors,
}) {
  const camBg = useWebcam ? 'transparent' : (isVmix ? 'transparent' : '#00FF00');
  const hasBong1Data = showBong && bongData?.legs && Object.values(bongData.legs).some(v => String(v || '').trim() !== '');
  const hasBong2Data = showBong && showDualBong && bongDataGuest?.legs && Object.values(bongDataGuest.legs).some(v => String(v || '').trim() !== '');
  const anyBongVisible = hasBong1Data || hasBong2Data;
  let startStr = '';
  if (startTime) {
    try { startStr = new Date(startTime).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }); } catch {}
  }

  return (
    <div
      data-testid="studio-frame"
      style={{ position: 'absolute', inset: 0, backgroundColor: 'transparent', overflow: 'hidden' }}
    >
      <style>{`
        @keyframes studio-slide-right { from { transform: translateX(110%); } to { transform: translateX(0); } }
        @keyframes studio-slide-up { from { transform: translateY(180%); } to { transform: translateY(0); } }
        @keyframes studio-ticker-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>

      {/* Frame background built as 4 panels around the camera hole,
          so /vmix gets a true alpha-transparent camera window */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: STUDIO_CAM_RECT.top, backgroundColor: '#050F23' }} />
      <div style={{ position: 'absolute', top: STUDIO_CAM_RECT.top, left: 0, width: STUDIO_CAM_RECT.left, height: STUDIO_CAM_RECT.height, backgroundColor: '#050F23' }} />
      <div style={{ position: 'absolute', top: STUDIO_CAM_RECT.top, left: `calc(${STUDIO_CAM_RECT.left} + ${STUDIO_CAM_RECT.width})`, right: 0, height: STUDIO_CAM_RECT.height, backgroundColor: '#050F23' }} />
      <div style={{ position: 'absolute', top: `calc(${STUDIO_CAM_RECT.top} + ${STUDIO_CAM_RECT.height})`, left: 0, right: 0, bottom: 0, backgroundColor: '#050F23' }} />

      {/* Subtle accent frame lines */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, var(--theme-gradient-line), var(--theme-gradient-line-subtle), transparent)' }} />

      {/* Camera window (transparent hole / chroma green) */}
      <div
        data-testid="studio-camera-window"
        style={{
          position: 'absolute', ...STUDIO_CAM_RECT,
          backgroundColor: camBg, borderRadius: '10px',
          border: '2px solid var(--theme-panel-border)',
          boxShadow: `0 0 30px rgba(var(--theme-accent-rgb), 0.12)`,
        }}
      />

      {/* Info strip below camera. Second line content: bong > crawl > weather > race info.
          When a message is active it gets its own top line and the rest moves to line two. */}
      <div
        data-testid="studio-info-strip"
        style={{
          position: 'absolute', left: STUDIO_CAM_RECT.left, width: STUDIO_CAM_RECT.width,
          top: 'calc(2.5vh + 76vh + 2vh)', height: showMessage && messageText ? '13vh' : '9vh',
          backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
          borderRadius: '10px', padding: '0 24px',
          display: 'flex',
          flexDirection: showMessage && messageText ? 'column' : 'row',
          alignItems: showMessage && messageText ? 'stretch' : 'center',
          gap: showMessage && messageText ? '0' : '24px',
          animation: 'studio-slide-up 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          overflow: 'hidden',
        }}
      >
        {showMessage && messageText && (
          <div style={{
            display: 'flex', alignItems: 'center', flex: 1, minHeight: 0,
            borderBottom: '1px solid var(--theme-panel-border-faint)',
          }}>
            <StudioMessage messageText={messageText} fullWidth />
          </div>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '24px', flex: 1, minHeight: 0, overflow: 'hidden',
        }}>
        {showCrawl && crawlText ? (
          <StudioCrawl crawlText={crawlText} />
        ) : showWeather && weatherData ? (
          <StudioWeatherStrip weatherData={weatherData} />
        ) : showOdds && sortedHorses.length > 0 ? (
          <StudioOddsTicker
            sortedHorses={sortedHorses} gameType={gameType} legNumber={legNumber}
            flashingHorses={flashingHorses} renderDistArrow={renderDistArrow}
          />
        ) : (
          <>
            <div style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontSize: '2rem', fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--theme-accent)', whiteSpace: 'nowrap',
            }}>
              <span style={{
                display: 'inline-block', width: '13px', height: '13px', borderRadius: '50%',
                backgroundColor: '#DC2626', marginRight: '14px', verticalAlign: 'middle',
              }} />
              Direkte
            </div>
            <div style={{ width: '2px', height: '50%', backgroundColor: 'var(--theme-panel-border-subtle)' }} />
            <div style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.8rem', fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.05em', color: '#FFFFFF', whiteSpace: 'nowrap',
            }}>
              {trackName || 'Sørlandets Travpark'}
            </div>
            <div style={{ flex: 1 }} />
            {gameType && (
              <div style={{
                fontFamily: "'Inter', sans-serif", fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)',
                whiteSpace: 'nowrap',
              }}>
                <span style={{ color: 'var(--theme-accent)', fontWeight: 700 }}>{gameType}-{legNumber}</span>
                {raceDistance > 0 && ` • ${raceDistance}m`}
                {startMethod && ` • ${startMethod}`}
                {startStr && ` • Kl. ${startStr}`}
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Right graphics column */}
      <div
        data-testid="studio-side-column"
        style={{
          position: 'absolute', left: 'calc(1.5vw + 76vw + 1.5vw)', top: STUDIO_CAM_RECT.top,
          width: 'calc(100vw - 1.5vw - 76vw - 1.5vw - 1.5vw)', height: '87vh',
          display: 'flex', flexDirection: 'column', gap: '1.6vh',
          animation: 'studio-slide-right 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Header: logo + track */}
        <div style={{
          backgroundColor: 'var(--theme-panel-bg)', border: '2px solid var(--theme-panel-border)',
          borderRadius: '10px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0,
          background: `linear-gradient(135deg, var(--theme-header-gradient) 0%, var(--theme-panel-bg) 100%)`,
        }}>
          <TravparkLogo height={48} sommertravetMode={sommertravetMode} />
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.25rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.06em', color: '#FFFFFF', lineHeight: 1.1,
          }}>
            Sørlandets<br />Travpark
          </div>
        </div>

        <StudioClock startTime={startTime} />

        {anyBongVisible ? (
          <div
            data-testid="studio-bong-stack"
            style={{
              display: 'flex', flexDirection: 'column', gap: '1.6vh',
              flex: 1, minHeight: 0, overflow: 'hidden',
            }}
          >
            {hasBong1Data && (
              <StudioBongCard
                bongData={bongData} bongSelectedLegs={bongSelectedLegs} gameType={gameType}
              />
            )}
            {hasBong2Data && (
              <StudioBongCard
                bongData={bongDataGuest} bongSelectedLegs={bongSelectedLegs} gameType={gameType}
              />
            )}
          </div>
        ) : showOdds ? (
          <StudioOddsPanel
            sortedHorses={sortedHorses} gameType={gameType} legNumber={legNumber}
            flashingHorses={flashingHorses} renderDistArrow={renderDistArrow}
            allRaces={allRaces} currentLeg={currentLeg}
          />
        ) : (
          <div style={{ flex: 1 }} />
        )}

        {sponsors?.length > 0 && <StudioSponsorCard sponsors={sponsors} />}

        <StudioWeatherCard weatherData={weatherData} />
      </div>
    </div>
  );
}
